import { SoundCategory } from '@/systems';

/**
 * Sound configuration interface
 */
export interface SoundConfig {
  src: string;
  category: SoundCategory;
  loop?: boolean;
  volume?: number;
  preload?: boolean;
}

/**
 * Sound configuration for the game
 * Contains all sound assets organized by category
 */
export const soundConfig = {
  /**
   * Music tracks
   */
  music: {
    musicBox: {
      src: 'assets/audio/bgm/bg-music-box.mp3',
      category: SoundCategory.MUSIC,
      loop: true,
      volume: 0.5,
      preload: true,
    },
    piano: {
      src: 'assets/audio/bgm/bg-piano.mp3',
      category: SoundCategory.MUSIC,
      loop: true,
      volume: 0.1,
      preload: true,
    },
    standingby: {
      src: 'assets/audio/bgm/music-standing-by.mp3',
      category: SoundCategory.MUSIC,
      loop: true,
      volume: 1,
      preload: true,
    },
    tense: {
      src: 'assets/audio/bgm/music-tense.mp3',
      category: SoundCategory.MUSIC,
      loop: true,
      volume: 1,
      preload: true,
    },
    superTense: {
      src: 'assets/audio/bgm/music-super-tense.mp3',
      category: SoundCategory.MUSIC,
      loop: true,
      volume: 1,
      preload: true,
    },

    badChaseScene: {
      src: 'assets/audio/bgm_ogg/bad_chase_scene.ogg',
      category: SoundCategory.MUSIC,
      loop: true,
      volume: 1,
      preload: true,
    },
    badEnding: {
      src: 'assets/audio/bgm_ogg/bad_ending.ogg',
      category: SoundCategory.MUSIC,
      loop: true,
      volume: 1,
      preload: true,
    },
    happyA: {
      src: 'assets/audio/bgm_ogg/happy_a.ogg',
      category: SoundCategory.MUSIC,
      loop: true,
      volume: 1,
      preload: true,
    },
    happyB: {
      src: 'assets/audio/bgm_ogg/happy_b.ogg',
      category: SoundCategory.MUSIC,
      loop: true,
      volume: 1,
      preload: true,
    },
    secretEnding: {
      src: 'assets/audio/bgm_ogg/secret_ending.ogg',
      category: SoundCategory.MUSIC,
      loop: true,
      volume: 1,
      preload: true,
    },

  },

  /**
   * Sound effects
   */
  sfx: {
    chicken: {
      src: 'assets/audio/sfx/chicken.mp3',
      category: SoundCategory.SFX,
      volume: 0.7,
    },
    tinnitus: {
      src: 'assets/audio/bgm/tinnitus.mp3',
      category: SoundCategory.SFX,
      volume: 0.7,
    },
    fishingReel: {
      src: 'assets/audio/sfx/fishing-reel.mp3',
      category: SoundCategory.SFX,
      volume: 0.7,
    },
    bladePierce: {
      src: 'assets/audio/sfx/blade-pierce.mp3',
      category: SoundCategory.SFX,
      volume: 0.7,
    },
    static: {
      src: 'assets/audio/sfx/static.mp3',
      category: SoundCategory.SFX,
      volume: 0.7,
    },
    click : {
      src: 'assets/audio/sfx/click.mp3',
      category: SoundCategory.SFX,
      volume: 0.7,
    },
    doorKnock : {
      src: 'assets/audio/sfx/door-knock.mp3',
      category: SoundCategory.SFX,
      volume: 1,
    },
    burn: {
      src: 'assets/audio/sfx/burn.mp3',
      category: SoundCategory.SFX,
      volume: 0.6,
    },
    doorSlam: {
      src: 'assets/audio/sfx/door-slam.mp3',
      category: SoundCategory.SFX,
      volume: 0.8,
    },
    openDoor: {
      src: 'assets/audio/sfx/open-door.mp3',
      category: SoundCategory.SFX,
      volume: 0.8,
    },
    heart: {
      src: 'assets/audio/sfx/heart-beat.mp3',
      category: SoundCategory.SFX,
      volume: 0.8,
    },
    girlScream: {
      src: 'assets/audio/sfx/girl-scream.mp3',
      category: SoundCategory.SFX,
      volume: 0.7,
    },
    knifeStab: {
      src: 'assets/audio/sfx/knife-stab.mp3',
      category: SoundCategory.SFX,
      volume: 0.7,
    },
    monsterLaugh: {
      src: 'assets/audio/sfx/monster-laugh.mp3',
      category: SoundCategory.SFX,
      volume: 0.6,
    },
    monsterScream: {
      src: 'assets/audio/sfx/monster-scream.mp3',
      category: SoundCategory.SFX,
      volume: 0.8,
    },
    orchestraFreak: {
      src: 'assets/audio/sfx/orhcestra-freak.mp3',
      category: SoundCategory.SFX,
      volume: 0.6,
    },
    swordSeathe: {
      src: 'assets/audio/sfx/sword-seathe.mp3',
      category: SoundCategory.SFX,
      volume: 0.5,
    },
    womanScream: {
      src: 'assets/audio/sfx/woman-scream.mp3',
      category: SoundCategory.SFX,
      volume: 0.7,
    },
    eating: {
      src: 'assets/audio/sfx/eating.mp3',
      category: SoundCategory.SFX,
      volume: 1,
    },
    punch: {
      src: 'assets/audio/sfx/punch.mp3',
      category: SoundCategory.SFX,
      volume: 0.8,
    },
    rowdyCrowd: {
      src: 'assets/audio/sfx/rowdy-crowd.mp3',
      category: SoundCategory.SFX,
      volume: 0.8,
    },
    running: {
      src: 'assets/audio/sfx/running.mp3',
      category: SoundCategory.SFX,
      volume: 15,
    },
    churchBell: {
      src: 'assets/audio/sfx/church-bell.mp3',
      category: SoundCategory.SFX,
      volume: 0.8,
    },
    churchOrgan: {
      src: 'assets/audio/sfx/church-organ.mp3',
      category: SoundCategory.SFX,
      volume: 0.8,
    },
  },

  /**
   * Voice or dialog sounds
   */
  voice: {
    // Example voice line
    // 'characterName1': {
    //   src: 'assets/audio/character-name-1.mp3',
    //   category: SoundCategory.VOICE,
    //   volume: 0.5,
    // },
  },

  /**
   * Ambient sounds
   */
  ambient: {
    // Example ambient sound
    // 'forestAmbience': {
    //   src: 'assets/audio/forest-ambience.mp3',
    //   category: SoundCategory.AMBIENT,
    //   loop: true,
    //   volume: 0.3,
    // },
  },
};

// Export specific sound categories for easier access
export const musicSounds = soundConfig.music;
export const sfxSounds = soundConfig.sfx;
export const voiceSounds = soundConfig.voice;
export const ambientSounds = soundConfig.ambient;

/**
 * Get all sounds as a flat object for easy loading
 */
export function getAllSounds(): Record<string, SoundConfig> {
  const allSounds: Record<string, SoundConfig> = {};

  // Helper to add sounds with prefixed keys
  const addSounds = (sounds: Record<string, SoundConfig>, prefix: string): void => {
    Object.entries(sounds).forEach(([key, config]) => {
      allSounds[`${prefix}_${key}`] = config;
    });
  };

  addSounds(soundConfig.music, 'music');
  addSounds(soundConfig.sfx, 'sfx');
  addSounds(soundConfig.voice, 'voice');
  addSounds(soundConfig.ambient, 'ambient');

  return allSounds;
}
